import { useState } from "react";
import { Sparkline } from "../charts/Sparkline.js";
import type { ChartPoint } from "../api/charts.js";
import type { MetricId } from "../charts/METRICS.js";
import { METRICS } from "../charts/METRICS.js";

export interface MetricCardProps {
  metricId: MetricId;
  current: number | null;
  previous: number | null;
  comparisonLabel: string;
  sparkline: ChartPoint[];
  description?: string;
  formula?: string;
  onClick?: () => void;
}

export function MetricCard({
  metricId, current, previous, comparisonLabel, sparkline,
  description, formula, onClick,
}: MetricCardProps) {
  const [tipOpen, setTipOpen] = useState(false);
  const meta = METRICS[metricId];
  const clickable = !!onClick;

  // Fallback: если description/formula не пришли в props — берём из METRICS[metricId].
  // Явный prop переопределяет дефолт из METRICS.
  const finalDescription = description ?? ("description" in meta ? meta.description : undefined);
  const finalFormula = formula ?? ("formula" in meta ? meta.formula : undefined);

  const delta = computeDelta(current, previous);
  const isGood = delta == null
    ? null
    : (delta >= 0) === !(("invertGoodSign" in meta) && meta.invertGoodSign);
  const arrow = delta == null ? "—" : delta > 0 ? "▲" : delta < 0 ? "▼" : "—";
  const deltaPct = delta == null
    ? "—"
    : `${(Math.abs(delta) * 100).toFixed(1).replace(".", ",")}%`;
  const deltaColor = isGood == null
    ? "var(--text-muted)"
    : isGood ? "var(--status-good-fg)" : "var(--status-bad-fg)";

  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-card)",
        boxShadow: "var(--shadow-card)",
        padding: 16,
        cursor: clickable ? "pointer" : "default",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.18s ease",
      }}
      onMouseEnter={(e) => {
        if (clickable) {
          e.currentTarget.style.boxShadow = "var(--shadow-card-hover)";
          e.currentTarget.style.borderColor = "var(--border-strong)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }
      }}
      onMouseLeave={(e) => {
        if (clickable) {
          e.currentTarget.style.boxShadow = "var(--shadow-card)";
          e.currentTarget.style.borderColor = "var(--border-default)";
          e.currentTarget.style.transform = "translateY(0)";
        }
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: meta.seriesColor, opacity: 0.85,
      }} />
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 12, color: "var(--text-secondary)", marginBottom: 8,
      }}>
        <span>{meta.label}</span>
        {(finalDescription || finalFormula) && (
          <span
            onMouseEnter={() => setTipOpen(true)}
            onMouseLeave={() => setTipOpen(false)}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 14, height: 14, borderRadius: "50%",
              background: "var(--surface-elevated)", color: "var(--text-muted)",
              fontSize: 9, fontWeight: 700, cursor: "help",
            }}
          >ⓘ</span>
        )}
        {tipOpen && (finalDescription || finalFormula) && (
          <div style={{
            position: "absolute", top: 30, left: 16, zIndex: 10,
            background: "var(--surface-card)", border: "1px solid var(--border-default)",
            borderRadius: 6, padding: "8px 12px", boxShadow: "var(--shadow-modal)",
            fontSize: 12, color: "var(--text-primary)", maxWidth: 280, lineHeight: 1.5,
          }}>
            {finalDescription && <div>{finalDescription}</div>}
            {finalFormula && (
              <div style={{
                marginTop: 6, color: "var(--text-secondary)",
                fontFamily: "var(--mono)", fontSize: 11,
              }}>= {finalFormula}</div>
            )}
          </div>
        )}
      </div>
      <div style={{
        fontSize: 32, fontWeight: 700, color: "var(--text-primary)",
        fontVariantNumeric: "tabular-nums", lineHeight: 1.1,
      }}>
        {meta.format(current)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
        <span style={{
          color: deltaColor, fontSize: 14, fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}>{arrow} {deltaPct}</span>
        <span style={{ color: "var(--text-secondary)", fontSize: 11 }}>{comparisonLabel}</span>
      </div>
      {sparkline.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Sparkline metricId={metricId} points={sparkline} height={28} />
        </div>
      )}
    </div>
  );
}

function computeDelta(current: number | null, previous: number | null): number | null {
  if (current == null || previous == null || previous === 0) return null;
  return (current - previous) / previous;
}
